import React from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { Box, createTheme, Grid, Stack, ThemeProvider, Typography, useTheme } from "@mui/material"
import { BotIcon } from "../../components/BotIcon"
import { kmAskBot, KmResponse } from "../../km"
import { ChatBotSelectionMenu } from "../../components/ChatBotSelectionMenu"
import { TooltipButton } from "../../components/TooltipButton"
import LanguageIcon from "@mui/icons-material/Language"
import MemoryIcon from "@mui/icons-material/Memory"
import CloseIcon from "@mui/icons-material/Close"
import MinimizeIcon from "@mui/icons-material/Minimize"
import { useChatState } from "../../hooks/useChatState"
import { useLocalStorage } from "src/utils/useLocalStorage"
import { useModels } from "src/km/hooks/useModels"
import { ChatBotUserInput } from "src/km/components/ChatBotUserInput"
import { ChatBotMessageList } from "src/km/components/ChatBotMessageList"
import { Language } from "src/utils/useLanguageList"

const ChatBotPage = () => {
  const companyName = "Eure!Car"
  const { chatId } = useParams()
  const [searchParams] = useSearchParams()
  const parentOrigin = searchParams.get("parentOrigin")
  const title = `${companyName} Assistant`
  const selectedTaxonomy = { name: "Exfluency Knowledge Base", code: "confluence" }
  const botIcon = "/km/chat-icon.svg"
  const companyId = chatId
  const helloMessage = "Hi! I'm your technical assistant. How can I help you?"
  const languages = [
    { languageId: "EN_GB", name: "English" },
    { languageId: "FR", name: "French" },
    { languageId: "DE", name: "German" },
    { languageId: "ES", name: "Spanish" },
    { languageId: "RO", name: "Romanian" },
    { languageId: "SV", name: "Swedish" }
  ]

  const [lang, setLang] = useLocalStorage<Language>("chatbot.lang", languages[0], (value: Language) =>
    languages.some((l) => l.languageId === value.languageId)
  )
  const { query, setQuery, loading, messageState, handleSubmit, clear, noQuestionsYet } = useChatState({
    storageKey: `chat.history.${companyId}.chat`,
    defaultMessage: () => helloMessage,
    customSelection: (suggestions, messages) => {
      if (!suggestions) {
        return undefined
      }

      // Get all previously selected provider companies
      const selectedCompanies = new Set(messages.filter((msg) => msg.provider).map((msg) => msg.provider!.company))

      // Collect all providers from all suggestions
      const allProviders = suggestions.products.flatMap((suggestion) => suggestion.providers)

      // Find the first provider that hasn't been selected before
      return allProviders.find((provider) => !selectedCompanies.has(provider.company))
    }
  })
  const { models, model, setModel } = useModels({ endpoint: "/api/km/chat-bot/models" })

  const { palette } = useTheme()
  const theme = createTheme({
    palette: {
      ...palette,
      primary: {
        light: "#FFD95D",
        main: "#FFC300",
        contrastText: "#222222",
        400: "#FFCE2E"
      },
      text: {
        primary: "#222222"
      }
    }
  })

  if (!model || !companyId || !parentOrigin) {
    return <></>
  }

  // Create a wrapper for handleSubmit that calls the hook's handleSubmit with postQuestion
  const handleSubmitWrapper = (e: any) => {
    handleSubmit(e, postQuestion)
  }

  const postQuestion = (question: string, history: string[]): Promise<KmResponse> =>
    kmAskBot({
      question: question,
      history: history,
      model: model.model,
      taxonomy: selectedTaxonomy.code,
      lang: lang.languageId,
      company_id: companyId,
      realTaxonomyKey: undefined, // Bot doesn't have access to company portal properties
      genre: undefined
    }).then((response) => response.data)

  const notifyParent = (action: string, data: any) => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        {
          action: action,
          data: data
        },
        parentOrigin
      )
    }
  }

  const minimize = () => {
    notifyParent("minimize", {})
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column" }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            backgroundColor: theme.palette.primary.main, // Bright yellow background
            px: { xs: 2, sm: 3 },
            py: 2
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <BotIcon botIcon={botIcon} size="medium" />
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.contrastText,
                fontWeight: "bold"
              }}
            >
              {title}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <TooltipButton title="Minimize chat" onClick={minimize} icon={<MinimizeIcon fontSize="medium" />} />
            <TooltipButton
              title="Close & clear chat"
              onClick={() => {
                minimize()
                clear()
              }}
              icon={<CloseIcon fontSize="medium" />}
            />
          </Stack>
        </Stack>
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <Grid item display="flex" flexDirection="column" xs={12} sx={{ height: "100%", width: "100%" }}>
            <Box
              sx={{
                p: { xs: 1, sm: 2, md: 4 },
                flexGrow: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white"
              }}
            >
              <ChatBotMessageList
                messages={messageState.messages}
                botIcon={botIcon}
                loading={loading}
                companyName={companyName}
              />
            </Box>
            <Box
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                px: { xs: 1, sm: 2, md: 4 },
                background: "#F5F3F3",
                boxShadow: "inset 0px 1px 10px #00000029",
                flexShrink: 0
              }}
            >
              <ChatBotUserInput
                query={query}
                setQuery={setQuery}
                loading={loading}
                placeholder={"Ask me anything"}
                initialPlaceholder={"Ask me anything"}
                modelSelected={!!model}
                noQuestionsYet={noQuestionsYet}
                handleSubmit={handleSubmitWrapper}
              />
            </Box>

            {/* Footer with language selection and controls */}
            {
              <Box
                sx={{
                  backgroundColor: theme.palette.primary.contrastText,
                  p: { xs: 2, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flexShrink: 0
                }}
              >
                {/* Footer Controls */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <Box sx={{ display: "flex" }}>
                    <ChatBotSelectionMenu
                      selectedItem={lang}
                      setSelectedItem={setLang}
                      items={languages}
                      getItemId={(language) => language.languageId}
                      getItemName={(language) => language.name}
                      title="Choose a language"
                      icon={<LanguageIcon fontSize="small" sx={{ width: "1.5em", height: "1.5em" }} />}
                    />
                    <ChatBotSelectionMenu
                      selectedItem={model}
                      setSelectedItem={setModel}
                      items={models}
                      getItemId={(modelItem) => modelItem.model}
                      getItemName={(modelItem) => modelItem.name}
                      title="Choose a model"
                      icon={<MemoryIcon fontSize="small" sx={{ width: "1.5em", height: "1.5em" }} />}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#797878",
                      fontSize: "12px",
                      cursor: "pointer",
                      "&:hover": {
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    Cookie & Privacy Policy
                  </Typography>
                </Box>
              </Box>
            }
          </Grid>
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export default ChatBotPage
